import { Link } from 'react-router-dom';
import { useState } from 'react';
import { ChevronRight, Leaf } from 'lucide-react';
import { formatPercentPoints, type StrainSummary } from '@shared';
import { resolveCardThumbnailUrl } from '../lib/cardThumbnail';

/**
 * Display gate for catalogue-card terpene text.
 * Only finite positive values are treated as meaningful; null, zero, negatives,
 * NaN, and ±Infinity do not render terpene content.
 */
export function hasMeaningfulTerpenePercent(
  value: number | null | undefined,
): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** Compact horizontal Library row. */
export function StrainCard({ strain }: { strain: StrainSummary }): JSX.Element {
  const title = strain.name ?? strain.productName ?? 'Untitled strain';
  const classLabel =
    strain.strainClassSourceText ?? strain.cultivarType ?? strain.category;

  const potencyParts = [`${formatPercentPoints(strain.thcPercent)} THC`];
  if (strain.cbdPercent !== null) {
    potencyParts.push(`${formatPercentPoints(strain.cbdPercent)} CBD`);
  }
  if (hasMeaningfulTerpenePercent(strain.totalTerpenesPercent)) {
    potencyParts.push(
      `${formatPercentPoints(strain.totalTerpenesPercent)} terpenes`,
    );
  }

  const candidates = {
    spreadsheetUrl: strain.spreadsheetThumbnailUrl,
    localPrimaryUrl: strain.localPrimaryThumbnailUrl,
    firstLocalUrl: strain.firstLocalThumbnailUrl,
  };

  const [failedUrls, setFailedUrls] = useState<Set<string>>(() => new Set());
  const thumbSrc = resolveCardThumbnailUrl(candidates, failedUrls);

  const onThumbError = (): void => {
    if (!thumbSrc) return;
    setFailedUrls((prev) => {
      if (prev.has(thumbSrc)) return prev;
      const next = new Set(prev);
      next.add(thumbSrc);
      return next;
    });
  };

  return (
    <Link to={`/strains/${strain.id}`} className="strain-row">
      <div className="strain-row__thumb" aria-hidden={!thumbSrc}>
        {thumbSrc ? (
          <img src={thumbSrc} alt="" onError={onThumbError} />
        ) : (
          <span className="strain-row__placeholder">
            <Leaf size={20} strokeWidth={1.6} />
          </span>
        )}
      </div>

      <div className="strain-row__body">
        <div className="strain-row__title-line">
          <h3 className="strain-row__name">{title}</h3>
        </div>
        {classLabel ? (
          <div className="strain-row__class">{classLabel}</div>
        ) : null}
        <div className="strain-row__potency">{potencyParts.join(' | ')}</div>
      </div>

      <span className="strain-row__chevron" aria-hidden="true">
        <ChevronRight size={16} strokeWidth={2} />
      </span>
    </Link>
  );
}
